import React, { FC, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import Box from '@mui/material/Box'

import { SECTION_ORDER } from '../../constants'
import { useAppSelector } from '../../store/hooks'

import { LargeWidgetsSection } from './widgetsComponents'
import { SmallWidgetsSection } from './widgetsComponents'
import { MediumWidgetsSection } from './widgetsComponents'

// Simple wrapper that makes a section sortable

const SortableSection: FC<{ id: string; children: React.ReactNode; disabled: boolean }> = ({
  id,
  children,
  disabled,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  // ✅ Only translate, ignore scale
  const transformStyle = transform
    ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
    : undefined

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: transformStyle,
        transition,
      }}
      sx={{
        zIndex: isDragging ? 1 : 'auto',
        cursor: disabled ? 'default' : 'grab',
      }}
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
    >
      {children}
    </Box>
  )
}

export const WidgetsSection: FC = () => {
  const sectionMap = {
    small: <SmallWidgetsSection />,
    large: <LargeWidgetsSection />,
    medium: <MediumWidgetsSection />,
  }

  type SectionKey = keyof typeof sectionMap

  const { customize } = useAppSelector(({ dashboard }) => dashboard)

  const storedSectionOrder = localStorage.getItem(SECTION_ORDER)
  const initSectionOrder = storedSectionOrder
    ? (JSON.parse(storedSectionOrder) as SectionKey[])
    : (Object.keys(sectionMap) as SectionKey[])

  const [sections, setSections] = useState<SectionKey[]>(initSectionOrder)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    if (!customize) {
      return
    }

    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sections.indexOf(active.id as SectionKey)
    const newIndex = sections.indexOf(over.id as SectionKey)
    if (oldIndex === -1 || newIndex === -1) return

    const newOrder = arrayMove(sections, oldIndex, newIndex)
    setSections(newOrder)
    localStorage.setItem(SECTION_ORDER, JSON.stringify(newOrder))
  }

  return (
    <Box className="dashboard-widgets">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
      >
        <SortableContext items={sections} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sections.map((sectionName) => (
              <SortableSection key={sectionName} id={sectionName} disabled={!customize}>
                {sectionMap[sectionName]}
              </SortableSection>
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  )
}
